// frontend/src/components/formularios/TablaEvaluacionMensual.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Chip,
  Grid,
  Card,
  CardContent,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  Calculate as CalculateIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import formularioService from '../../services/formularioService';

const TablaEvaluacionMensual = ({ 
  campo, 
  formularioPadre, 
  value = {}, 
  onChange,
  readOnly = false 
}) => {
  const { configuracion } = campo;
  const [objetivosSeleccionados, setObjetivosSeleccionados] = useState([]);
  const [evaluaciones, setEvaluaciones] = useState(value || {});
  const [objetivosDisponibles, setObjetivosDisponibles] = useState([]);
  const [mostrarLeyenda, setMostrarLeyenda] = useState(false);
  const [estadisticas, setEstadisticas] = useState({});
  
  useEffect(() => {
    if (formularioPadre) {
      cargarObjetivosDisponibles();
    }
  }, [formularioPadre]);
  
  useEffect(() => {
    // Cargar objetivos ya evaluados
    const objetivosIds = Object.keys(value);
    if (objetivosIds.length > 0) {
      setObjetivosSeleccionados(objetivosIds);
      setEvaluaciones(value);
    }
  }, [value]);
  
  useEffect(() => {
    // Calcular estadísticas cuando cambien las evaluaciones
    calcularEstadisticas();
  }, [evaluaciones]);
  
  const cargarObjetivosDisponibles = async () => {
    try {
      const response = await formularioService.getObjetivosDisponibles(formularioPadre.id);
      
      if (response.success && response.data) {
        setObjetivosDisponibles(response.data.objetivos_disponibles || []);
      }
    } catch (error) {
      console.error('Error al cargar objetivos:', error);
      // Fallback local
      procesarObjetivosLocal();
    }
  };
  
  const procesarObjetivosLocal = () => {
    if (!formularioPadre.respuestas) return;
    
    const areas = formularioPadre.respuestas.areas_intervencion_neuro || [];
    const objetivos = [];
    
    areas.forEach((area, areaIndex) => {
      const objetivosTexto = area.objetivos_especificos || '';
      const listaObjetivos = objetivosTexto
        .split('\n')
        .filter(obj => obj.trim());
      
      listaObjetivos.forEach((objetivo, objIndex) => {
        objetivos.push({
          id: `area_${areaIndex}_obj_${objIndex}`,
          area_nombre: area.area_intervenir,
          objetivo_texto: objetivo.trim(),
          numero: `${areaIndex + 1}.${objIndex + 1}`
        });
      });
    });
    
    setObjetivosDisponibles(objetivos);
  };
  
  const agregarObjetivo = (objetivoId) => {
    if (!objetivoId || objetivosSeleccionados.includes(objetivoId)) return;
    
    const objetivo = objetivosDisponibles.find(obj => obj.id === objetivoId);
    if (!objetivo) return;
    
    const nuevosSeleccionados = [...objetivosSeleccionados, objetivoId];
    setObjetivosSeleccionados(nuevosSeleccionados);
    
    const nuevasEvaluaciones = {
      ...evaluaciones,
      [objetivoId]: {
        objetivo_texto: objetivo.objetivo_texto,
        area_nombre: objetivo.area_nombre,
        ses1_fecha: '',
        ses1_puntaje: '',
        ses2_fecha: '',
        ses2_puntaje: '',
        promedio: 0,
        observacion: ''
      }
    };
    
    setEvaluaciones(nuevasEvaluaciones);
    onChange(nuevasEvaluaciones);
  };
  
  const eliminarObjetivo = (objetivoId) => {
    const nuevosSeleccionados = objetivosSeleccionados.filter(id => id !== objetivoId);
    setObjetivosSeleccionados(nuevosSeleccionados);
    
    const nuevasEvaluaciones = { ...evaluaciones };
    delete nuevasEvaluaciones[objetivoId];
    
    setEvaluaciones(nuevasEvaluaciones);
    onChange(nuevasEvaluaciones);
  };
  
  const handleCambioEvaluacion = (objetivoId, campo, valor) => {
    const evaluacionActual = evaluaciones[objetivoId] || {};
    const evaluacionActualizada = { ...evaluacionActual, [campo]: valor };
    
    // Calcular promedio si se actualizan puntajes
    if (campo.includes('puntaje')) {
      const puntaje1 = campo === 'ses1_puntaje' ? 
        parseFloat(valor) || 0 : parseFloat(evaluacionActual.ses1_puntaje) || 0;
      const puntaje2 = campo === 'ses2_puntaje' ? 
        parseFloat(valor) || 0 : parseFloat(evaluacionActual.ses2_puntaje) || 0;
      
      if (puntaje1 > 0 && puntaje2 > 0) {
        evaluacionActualizada.promedio = (puntaje1 + puntaje2) / 2;
        evaluacionActualizada.observacion = obtenerObservacionPorPromedio(evaluacionActualizada.promedio);
      }
    }
    
    const nuevasEvaluaciones = {
      ...evaluaciones,
      [objetivoId]: evaluacionActualizada
    };
    
    setEvaluaciones(nuevasEvaluaciones);
    onChange(nuevasEvaluaciones);
  };
  
  const obtenerObservacionPorPromedio = (promedio) => {
    if (promedio >= 3) return 'Logrado';
    if (promedio >= 2) return 'En Proceso';
    return 'Reprogramación';
  };
  
  const obtenerColorPorEstado = (observacion) => {
    switch (observacion) {
      case 'Logrado': return 'success';
      case 'En Proceso': return 'warning';
      case 'Reprogramación': return 'error';
      default: return 'default';
    }
  };
  
  const calcularEstadisticas = () => {
    const stats = {
      total: objetivosSeleccionados.length,
      evaluados: 0,
      logrados: 0,
      enProceso: 0,
      reprogramacion: 0,
      promedioGeneral: 0
    };
    
    let sumaPromedios = 0;
    let contadorEvaluados = 0;
    
    objetivosSeleccionados.forEach(objId => {
      const eval_ = evaluaciones[objId];
      if (eval_ && eval_.promedio > 0) {
        stats.evaluados++;
        contadorEvaluados++;
        sumaPromedios += eval_.promedio;
        
        if (eval_.observacion === 'Logrado') stats.logrados++;
        else if (eval_.observacion === 'En Proceso') stats.enProceso++;
        else if (eval_.observacion === 'Reprogramación') stats.reprogramacion++;
      }
    });
    
    if (contadorEvaluados > 0) {
      stats.promedioGeneral = (sumaPromedios / contadorEvaluados).toFixed(2);
    }
    
    setEstadisticas(stats);
  };
  
  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom>
        {campo.etiqueta}
      </Typography>
      
      {/* Selector de objetivos */}
      {!readOnly && objetivosSeleccionados.length < (configuracion.max_objetivos_mes || 15) && (
        <Box mb={2}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <FormControl fullWidth size="small">
                <InputLabel>Agregar objetivo a evaluar</InputLabel>
                <Select
                  value=""
                  onChange={(e) => agregarObjetivo(e.target.value)}
                  label="Agregar objetivo a evaluar"
                >
                  <MenuItem value="" disabled>
                    Seleccione un objetivo
                  </MenuItem>
                  {objetivosDisponibles
                    .filter(obj => !objetivosSeleccionados.includes(obj.id))
                    .map(obj => (
                      <MenuItem key={obj.id} value={obj.id}>
                        {obj.area_nombre} - {obj.objetivo_texto}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="outlined"
                startIcon={<InfoIcon />}
                onClick={() => setMostrarLeyenda(true)}
                fullWidth
              >
                Ver Leyenda
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}
      
      {/* Estadísticas */}
      {objetivosSeleccionados.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="textSecondary">
                  Total Objetivos
                </Typography>
                <Typography variant="h6">
                  {estadisticas.total}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="textSecondary">
                  Evaluados
                </Typography>
                <Typography variant="h6">
                  {estadisticas.evaluados}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="textSecondary">
                  Promedio General
                </Typography>
                <Typography variant="h6" color={
                  estadisticas.promedioGeneral >= 3 ? 'success.main' :
                  estadisticas.promedioGeneral >= 2 ? 'warning.main' : 'error.main'
                }>
                  {estadisticas.promedioGeneral}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip 
                    size="small" 
                    label={estadisticas.logrados} 
                    color="success" 
                  />
                  <Chip 
                    size="small" 
                    label={estadisticas.enProceso} 
                    color="warning" 
                  />
                  <Chip 
                    size="small" 
                    label={estadisticas.reprogramacion} 
                    color="error" 
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      
      {/* Tabla de evaluación */}
      {objetivosSeleccionados.length > 0 ? (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width="30%">Objetivo</TableCell>
                <TableCell align="center" width="12%">Fecha Ses.1</TableCell>
                <TableCell align="center" width="10%">Puntaje</TableCell>
                <TableCell align="center" width="12%">Fecha Ses.2</TableCell>
                <TableCell align="center" width="10%">Puntaje</TableCell>
                <TableCell align="center" width="10%">Promedio</TableCell>
                <TableCell align="center" width="12%">Observación</TableCell>
                {!readOnly && <TableCell align="center" width="4%">Acc.</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {objetivosSeleccionados.map((objId, index) => {
                const eval_ = evaluaciones[objId] || {};
                const objetivo = objetivosDisponibles.find(o => o.id === objId);
                
                return (
                  <TableRow key={objId}>
                    <TableCell>
                      <Typography variant="body2">
                        {objetivo?.objetivo_texto || eval_.objetivo_texto}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {objetivo?.area_nombre || eval_.area_nombre}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="date"
                        size="small"
                        value={eval_.ses1_fecha || ''}
                        onChange={(e) => handleCambioEvaluacion(objId, 'ses1_fecha', e.target.value)}
                        disabled={readOnly}
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        value={eval_.ses1_puntaje || ''}
                        onChange={(e) => handleCambioEvaluacion(objId, 'ses1_puntaje', e.target.value)}
                        disabled={readOnly}
                        inputProps={{ min: 1, max: 3, step: 0.5 }}
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="date"
                        size="small"
                        value={eval_.ses2_fecha || ''}
                        onChange={(e) => handleCambioEvaluacion(objId, 'ses2_fecha', e.target.value)}
                        disabled={readOnly}
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        value={eval_.ses2_puntaje || ''}
                        onChange={(e) => handleCambioEvaluacion(objId, 'ses2_puntaje', e.target.value)}
                        disabled={readOnly}
                        inputProps={{ min: 1, max: 3, step: 0.5 }}
                        fullWidth
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="bold">
                        {eval_.promedio ? eval_.promedio.toFixed(1) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {eval_.observacion && (
                        <Chip
                          size="small"
                          label={eval_.observacion}
                          color={obtenerColorPorEstado(eval_.observacion)}
                        />
                      )}
                    </TableCell>
                    {!readOnly && (
                      <TableCell>
                        <Tooltip title="Eliminar objetivo">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => eliminarObjetivo(objId)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Alert severity="info">
          No se han seleccionado objetivos para evaluar. 
          Use el selector superior para agregar objetivos.
        </Alert>
      )}
      
      {/* Diálogo de leyenda */}
      <Dialog 
        open={mostrarLeyenda} 
        onClose={() => setMostrarLeyenda(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {configuracion.leyenda?.titulo || 'Escala de Evaluación'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            {configuracion.leyenda?.items?.map((item, index) => (
              <Box key={index} display="flex" alignItems="center" gap={2} mb={1}>
                <Chip 
                  label={item.valor || item.codigo} 
                  color={
                    item.valor === 3 ? 'success' :
                    item.valor === 2 ? 'warning' :
                    item.valor === 1 ? 'error' : 'default'
                  }
                  sx={{ minWidth: 50 }}
                />
                <Typography variant="body2">
                  {item.descripcion}
                </Typography>
              </Box>
            ))}
          </Box>
          
          <Typography variant="caption" color="textSecondary">
            El promedio se calcula automáticamente con los puntajes de ambas sesiones.
            La observación se asigna según el promedio obtenido.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMostrarLeyenda(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TablaEvaluacionMensual;